import { useEffect, useMemo, useState } from "react";
import { useQuery } from "convex/react";
import { DayButton, DayPicker } from "react-day-picker";
import type { DayButtonProps } from "react-day-picker";
import "react-day-picker/style.css";
import { ChevronDown, TrendingDown, Wheat } from "lucide-react";
import { api } from "../../convex/_generated/api";
import { Card } from "../components/ui/Card";
import { ProgressRing } from "../components/ui/ProgressRing";
import { Skeleton } from "../components/ui/Skeleton";
import { AdjustmentsList } from "../components/progress/AdjustmentsList";
import { DataQualityCard } from "../components/progress/DataQualityCard";
import { ExpenditureCard } from "../components/progress/ExpenditureCard";
import { WeightTrendChart } from "../components/progress/WeightTrendChart";
import {
  WEIGHT_RANGES,
  buildProgressSummary,
  resolveRangeDays,
  type WeightRangeId,
} from "../components/progress/utils";
import { cn } from "../lib/cn";
import { formatNumber } from "../lib/format";
import { localDayStartMs, dayStartMs, addDays, formatDay } from "../lib/dayHelpers";
import type { Doc } from "../../convex/_generated/dataModel";

type ExerciseEntry = Doc<"exerciseEntries">;

type DayStatus = "on-track" | "over-goal" | "no-data";

const STATUS_DOT_CLASS: Record<DayStatus, string> = {
  "on-track": "bg-green-500",
  "over-goal": "bg-red-500",
  "no-data": "bg-text-muted/50",
};

function StatusDot({ status, size = "h-1.5 w-1.5" }: { readonly status: DayStatus; readonly size?: string }) {
  return <span aria-hidden className={cn("inline-block shrink-0 rounded-full", size, STATUS_DOT_CLASS[status])} />;
}

function formatCompactKcal(kcal: number): string {
  if (kcal >= 1000) {
    const thousands = kcal / 1000;
    return `${thousands >= 10 ? Math.round(thousands) : thousands.toFixed(1)}k`;
  }
  return `${Math.round(kcal)}`;
}

type MeterProps = {
  label: string;
  value: number | null;
  goal: number;
  color: string;
  testId: string;
};

function MacroMeter({ label, value, goal, color, testId }: MeterProps) {
  const unknown = value === null;
  const percentage = !unknown && goal > 0 ? Math.min(100, (value / goal) * 100) : 0;
  const ringColor = unknown ? "#617068" : color;
  return (
    <div className="flex flex-col items-center gap-2" data-testid={testId}>
      <ProgressRing value={percentage} size={82} stroke={8} color={ringColor}>
        <span className="text-sm font-semibold text-text">{unknown ? "—" : `${Math.round(value)}g`}</span>
      </ProgressRing>
      <span className="text-xs font-medium text-text-muted">{label}</span>
    </div>
  );
}

function WorkoutSummary({ entry }: { entry: ExerciseEntry }) {
  const metrics = [
    entry.durationMin !== undefined ? `${entry.durationMin} min` : null,
    entry.distanceM !== undefined && entry.distanceM > 0 ? `${(entry.distanceM / 1000).toFixed(1)} km` : null,
    entry.reps !== undefined ? `${entry.reps} reps` : null,
    entry.sets !== undefined ? `${entry.sets} sets` : null,
  ].filter((metric): metric is string => metric !== null);
  const calories = entry.finalBurnedKcal ?? entry.estimatedBurnedKcal;
  return (
    <Card className="flex items-center justify-between gap-3 p-4" data-testid={`progress-workout-${entry._id}`}>
      <div className="min-w-0">
        <p className="truncate text-sm font-semibold text-text">{entry.type}</p>
        <p className="mt-1 text-xs leading-relaxed text-text-muted">
          {metrics.length > 0 ? metrics.join(" · ") : "Workout logged"}
        </p>
      </div>
      <span className="shrink-0 text-sm font-semibold text-warning">{calories} kcal</span>
    </Card>
  );
}

type ProgressDayMetadata = {
  readonly consumedKcal: number;
  readonly netKcal: number;
  readonly weightKg: number | undefined;
  readonly hasData: boolean;
  readonly overGoal: boolean;
};

function ProgressDayButton({ metadataByDate, ...props }: DayButtonProps & { readonly metadataByDate: ReadonlyMap<number, ProgressDayMetadata> }) {
  const date = dayStartMs(props.day.date.getTime());
  const metadata = metadataByDate.get(date);
  const status: DayStatus =
    metadata === undefined || !metadata.hasData ? "no-data" : metadata.overGoal ? "over-goal" : "on-track";
  return (
    <DayButton {...props} title={metadata ? `${metadata.consumedKcal} kcal, net ${metadata.netKcal} kcal${metadata.weightKg === undefined ? "" : `, ${metadata.weightKg} kg`}` : "No nutrition data"}>
      <span className="text-xs font-semibold leading-none">{props.children}</span>
      <span className="text-[10px] font-medium leading-tight">
        {metadata?.hasData ? formatCompactKcal(metadata.consumedKcal) : "—"}
      </span>
      <span className="flex items-center gap-0.5 leading-none">
        <StatusDot status={status} />
        {metadata?.weightKg !== undefined && (
          <span className="text-[9px] font-medium text-text-muted">{metadata.weightKg}</span>
        )}
      </span>
    </DayButton>
  );
}

export default function Progress({ initialRangeId = "60d" }: { readonly initialRangeId?: WeightRangeId }) {
  const today = useMemo(() => localDayStartMs(Date.now()), []);
  const [rangeId, setRangeId] = useState<WeightRangeId>(initialRangeId);
  const rangeDays = resolveRangeDays(rangeId);
  const historyStart = useMemo(() => addDays(today, -(rangeDays - 1)), [today, rangeDays]);
  const [selectedDate, setSelectedDate] = useState(today);

  const range = useQuery(api.dashboard.getDateRange, {
    start: historyStart,
    end: today,
    clientStartDayStart: historyStart,
    clientEndDayStart: today,
  });
  const selectedSummary = useQuery(api.dashboard.getByDate, {
    date: selectedDate,
    clientDayStart: selectedDate,
  });
  const profile = useQuery(api.profile.getProfile, {});
  const engineState = useQuery(api.engine.getEngineState, {});
  const weights = useQuery(api.weight.list, { fromDate: historyStart, toDate: today, clientFromDayStart: historyStart, clientToDayStart: today });

  useEffect(() => {
    if (range !== undefined && weights !== undefined) {
      console.debug("[Frontend][Progress] Range loaded", { rangeId, days: range.length, weightEntries: weights.length });
    }
  }, [range, weights, rangeId]);

  const summary = useMemo(() => {
    if (engineState === undefined) return undefined;
    return buildProgressSummary({
      driftKgPerWeek: engineState?.driftKgPerWeek ?? null,
      weeklyChangeKg: engineState?.weeklyChangeKg ?? null,
    });
  }, [engineState]);

  const handleRangeChange = (next: WeightRangeId) => {
    console.debug("[Frontend][Progress] Range changed", { from: rangeId, to: next });
    setRangeId(next);
    const nextStart = addDays(today, -(resolveRangeDays(next) - 1));
    setSelectedDate((current) => (current < nextStart ? today : current));
  };

  const latestWeightByDate = useMemo(() => {
    const map = new Map<number, number>();
    if (weights) {
      for (const row of weights) {
        map.set(row.date, row.weightKg);
      }
    }
    return map;
  }, [weights]);

  const days = useMemo(() => {
    const result: number[] = [];
    for (let i = -(rangeDays - 1); i <= 0; i++) {
      result.push(addDays(today, i));
    }
    return result;
  }, [today, rangeDays]);

  const rangeByDate = useMemo(() => {
    const map = new Map<
      number,
      {
        date: number;
        consumedKcal: number;
        burnedKcal: number;
        netKcal: number;
        entryCount: number;
      }
    >();
    if (range) {
      for (const row of range) {
        map.set(row.date, row);
      }
    }
    return map;
  }, [range]);

  const selectedRing = useMemo(() => {
    const consumed = selectedSummary?.consumedKcal ?? 0;
    const goal = selectedSummary?.goalKcal ?? 0;
    const pct = goal > 0 ? (consumed / goal) * 100 : 0;
    const color =
      pct < 85 ? "#16a34a" : pct <= 100 ? "#f59e0b" : "#dc2626";
    return {
      consumed,
      goal,
      pct: Math.min(100, pct),
      color,
    };
  }, [selectedSummary]);
  const selectedWeightKg = latestWeightByDate.get(selectedDate);
  const metadataByDate = useMemo(() => {
    const map = new Map<number, ProgressDayMetadata>();
    for (const date of days) {
      const row = rangeByDate.get(date);
      const hasData = row !== undefined && row.entryCount > 0;
      map.set(date, {
        consumedKcal: row?.consumedKcal ?? 0,
        netKcal: row?.netKcal ?? 0,
        weightKg: latestWeightByDate.get(date),
        hasData,
        overGoal: hasData && row.consumedKcal > (profile?.dailyGoalKcal ?? 0),
      });
    }
    return map;
  }, [days, profile?.dailyGoalKcal, rangeByDate, latestWeightByDate]);

  return (
    <div data-testid="progress-container" className="flex flex-col gap-6">
      <div>
        <p className="text-sm leading-relaxed text-text-muted">Green days meet your calorie goal. Red days are over goal.</p>
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1" data-testid="progress-legend">
          <span className="flex items-center gap-1.5 text-xs text-text-muted"><StatusDot status="on-track" size="h-2 w-2" />Within goal</span>
          <span className="flex items-center gap-1.5 text-xs text-text-muted"><StatusDot status="over-goal" size="h-2 w-2" />Over goal</span>
          <span className="flex items-center gap-1.5 text-xs text-text-muted"><StatusDot status="no-data" size="h-2 w-2" />No logs</span>
        </div>
      </div>

      <Card className="flex items-start gap-3 p-4" data-testid="progress-summary">
        <span className="mt-0.5 shrink-0 rounded-full bg-accent-light p-2 text-accent">
          <TrendingDown size={18} />
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-text">Weekly summary</h3>
          {summary === undefined ? (
            <Skeleton className="mt-2 h-4 w-3/4" />
          ) : (
            <p className="mt-1 text-sm leading-relaxed text-text-muted" data-testid="progress-summary-sentence">
              {summary.sentence}
            </p>
          )}
        </div>
      </Card>

      <div className="flex gap-2" role="tablist" aria-label="Progress range" data-testid="progress-range-switcher">
        {WEIGHT_RANGES.map((option) => (
          <button
            key={option.id}
            type="button"
            role="tab"
            aria-selected={rangeId === option.id}
            data-testid={`progress-range-${option.id}`}
            onClick={() => handleRangeChange(option.id)}
            className={cn(
              "min-h-[44px] flex-1 rounded-xl px-2 text-sm font-semibold transition-all duration-200 ease-out",
              "active:scale-[0.98] select-none",
              rangeId === option.id ? "bg-accent text-white" : "bg-muted text-text-muted",
            )}
          >
            {option.label}
          </button>
        ))}
      </div>

      <WeightTrendChart rangeId={rangeId} onRangeChange={handleRangeChange} hideRangeControl />

      <Card className="overflow-hidden p-2" data-testid="progress-calendar">
        <DayPicker
          key={rangeId}
          mode="single"
          required
          selected={new Date(selectedDate)}
          onSelect={(date) => setSelectedDate(dayStartMs(date.getTime()))}
          defaultMonth={new Date(selectedDate)}
          startMonth={new Date(historyStart)}
          endMonth={new Date(today)}
          disabled={{ before: new Date(historyStart), after: new Date(today) }}
          modifiers={{
            onTrack: days.filter((date) => metadataByDate.get(date)?.hasData && !metadataByDate.get(date)?.overGoal).map((date) => new Date(date)),
            overGoal: days.filter((date) => metadataByDate.get(date)?.overGoal).map((date) => new Date(date)),
            noData: days.filter((date) => !metadataByDate.get(date)?.hasData).map((date) => new Date(date)),
          }}
          modifiersClassNames={{ onTrack: "progress-day-on-track", overGoal: "progress-day-over", noData: "progress-day-empty" }}
          components={{ DayButton: (props) => <ProgressDayButton {...props} metadataByDate={metadataByDate} /> }}
          classNames={{
            root: "w-full",
            months: "w-full",
            month: "w-full",
            month_grid: "w-full border-separate border-spacing-1",
            weekdays: "text-text-muted",
            weekday: "text-[10px] font-semibold uppercase",
            day: "h-[58px] w-[14.28%] rounded-xl border border-border p-0 text-center align-middle",
            day_button: "flex h-full w-full flex-col items-center justify-center gap-0.5 rounded-xl px-0 py-1 text-text active:scale-[0.98] select-none",
            selected: "border-2 border-accent bg-accent-light font-semibold",
            today: "font-bold ring-2 ring-inset ring-accent/50",
          }}
          showOutsideDays={false}
          aria-label={`${rangeDays}-day nutrition progress`}
        />
      </Card>

      <div data-testid="progress-day-detail">
        <h3 className="text-base font-medium text-text mb-3">
          Selected: {formatDay(selectedDate)}
        </h3>
        <Card className="p-6 flex flex-col items-center gap-4">
          {selectedSummary === undefined ? (
             <div className="flex w-full flex-col items-center gap-5" data-testid="progress-day-loading">
               <Skeleton className="h-36 w-36 rounded-full" />
                <div className="grid w-full grid-cols-3 gap-2">
                 <Skeleton className="h-20 rounded-2xl" />
                 <Skeleton className="h-20 rounded-2xl" />
                 <Skeleton className="h-20 rounded-2xl" />
               </div>
               <Skeleton className="h-8 w-full rounded-xl" />
               <div className="w-full border-t border-border pt-4 text-sm text-text-muted">Weight: <strong className="text-text">{selectedWeightKg === undefined ? "No measurement" : `${selectedWeightKg} kg`}</strong></div>
            </div>
          ) : (
            <>
              <div
                data-testid="progress-ring"
                className="flex justify-center"
              >
                <ProgressRing
                  value={selectedRing.pct}
                  size={150}
                  stroke={10}
                  color={selectedRing.color}
                >
                  <div className="text-center">
                    <div className="text-2xl font-bold text-text">
                      {formatNumber(selectedRing.consumed)}
                    </div>
                    <div className="text-xs text-text-muted">consumed</div>
                  </div>
                </ProgressRing>
              </div>
               <div className="flex w-full justify-around border-t border-border pt-4">
                <div className="text-center">
                  <div className="text-lg font-semibold text-text">
                    {selectedSummary.foodEntries.length}
                  </div>
                  <div className="text-xs text-text-muted">Meals</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-semibold text-text">
                    {selectedSummary.exerciseEntries.length}
                  </div>
                  <div className="text-xs text-text-muted">Exercise</div>
                 </div>
               </div>
               <div className="w-full border-t border-border pt-4 text-sm text-text-muted">
                  Weight: <strong className="text-text">{selectedWeightKg === undefined ? "No measurement" : `${selectedWeightKg} kg`}</strong>
                </div>
               <div className="w-full border-t border-border pt-4">
                 <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-text-muted">Macro balance</h3>
                   <div className="grid grid-cols-3 gap-2">
                    <MacroMeter label="Protein" value={selectedSummary.consumedProteinG} goal={selectedSummary.goalProteinG ?? 0} color="#2f855a" testId="progress-protein-ring" />
                    <MacroMeter label="Carbs" value={selectedSummary.consumedCarbsG} goal={selectedSummary.goalCarbsG ?? 0} color="#d97706" testId="progress-carbs-ring" />
                    <MacroMeter label="Fat" value={selectedSummary.consumedFatG} goal={selectedSummary.goalFatG ?? 0} color="#b45309" testId="progress-fat-ring" />
                 </div>
                 {/* Fibre is not a calorie macro: like Plan's Daily targets,
                     it sits in its own row, separate from the macro rings. */}
                 <div className="mt-3 flex items-center gap-2 rounded-xl bg-muted/60 px-3 py-2">
                   <Wheat size={14} className="shrink-0 text-text-muted" aria-hidden />
                   <p className="text-xs text-text-muted">
                     Fibre{" "}
                     <span className="font-semibold text-text" data-testid="progress-fibre">
                       {selectedSummary.consumedFiberG === null ? "—" : `${formatNumber(selectedSummary.consumedFiberG)} g`}
                     </span>
                     {selectedSummary.goalFiberG !== null ? ` of ${formatNumber(selectedSummary.goalFiberG)} g` : ""}{" "}
                     daily — tracked separately from the macro split.
                   </p>
                 </div>
               </div>
            </>
          )}
        </Card>
      </div>

      {selectedSummary !== undefined && selectedSummary.exerciseEntries.length > 0 && (
        <section data-testid="progress-workouts" className="flex flex-col gap-3">
          <div>
            <h3 className="text-base font-semibold text-text">Workouts</h3>
            <p className="mt-1 text-sm text-text-muted">Movement that shaped your day.</p>
          </div>
          <div className="flex flex-col gap-2">
            {selectedSummary.exerciseEntries.map((entry) => (
              <WorkoutSummary key={entry._id} entry={entry as ExerciseEntry} />
            ))}
          </div>
        </section>
      )}

      {selectedSummary !== undefined &&
        selectedSummary.foodEntries.length === 0 &&
        selectedSummary.exerciseEntries.length === 0 && (
          <Card className="border-dashed p-5" data-testid="progress-empty-state">
            <p className="text-sm font-semibold text-text">No activity logged for this day</p>
            <p className="mt-1 text-sm leading-relaxed text-text-muted">
              Choose another day above or head to Chat to log a meal or workout.
            </p>
          </Card>
        )}

      <details open className="group" data-testid="progress-details">
        <summary className="flex min-h-[44px] cursor-pointer items-center justify-between rounded-xl border border-border bg-surface px-4 py-3 text-sm font-semibold text-text select-none active:scale-[0.98] [&::-webkit-details-marker]:hidden">
          Details
          <ChevronDown size={16} aria-hidden className="transition-transform duration-200 ease-out group-open:rotate-180" />
        </summary>
        <div className="mt-4 flex flex-col gap-4">
          <ExpenditureCard />
          <DataQualityCard />
          <AdjustmentsList />
        </div>
      </details>
    </div>
  );
}
